import React from 'react';

const CursorMark = (props) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
    <path
      d="M3.5 5.5 13.8 2.2a1 1 0 0 1 1.26 1.26L11.8 13.8l6.2 6.2a1 1 0 0 1-.7 1.7H5.8a1.3 1.3 0 0 1-1.3-1.3V6.2c0-.28.09-.55.25-.77Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
  </svg>
);

const AntigravityMark = (props) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
    <path
      d="M12 3.5 20 20.5H4L12 3.5Z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
    <path
      d="M12 8.5v6"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      opacity="0.8"
    />
    <path
      d="M9.2 14.5h5.6"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      opacity="0.8"
    />
  </svg>
);

const ClaudeMark = (props) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
    <path
      d="M12 2.8 13.5 8l5.2-1.5-3.7 4 4 3.7L13.5 13 12 18.2 10.5 13 5.3 14.2l4-3.7-3.7-4L10.5 8 12 2.8Z"
      fill="currentColor"
    />
  </svg>
);

const ReplitMark = (props) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
    <rect x="6" y="6" width="12" height="4" rx="2" fill="currentColor" />
    <rect x="6" y="10" width="12" height="4" rx="2" fill="currentColor" opacity="0.85" />
    <rect x="6" y="14" width="12" height="4" rx="2" fill="currentColor" opacity="0.7" />
  </svg>
);

const VSCodeMark = (props) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
    <path
      d="M16.5 3.6 21 5.6v12.8l-4.5 2-7.6-7.2 7.6-7.6Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path
      d="M16.5 8.1 12.2 12l4.3 3.9"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path
      d="M7.2 8.7 3.4 12l3.8 3.3"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const editorCards = [
  { name: 'CURSOR', logoSrc: '/editor-logos/cursor.png', mark: CursorMark },
  { name: 'Windsurf', logoSrc: '/editor-logos/windsurf.png', mark: null },
  { name: 'Lovable', logoSrc: '/editor-logos/lovable.png', mark: null },
  { name: 'Bolt.new', logoSrc: '/editor-logos/bolt-new.png', mark: null },
  { name: 'Replit', logoSrc: '/editor-logos/replit.png', mark: ReplitMark },
  { name: 'And More...', mark: null },
];

const EditorCell = ({ name, Mark, logoSrc }) => {
  const [logoFailed, setLogoFailed] = React.useState(false);
  const isUpper = name === 'CURSOR';
  const isMore = name === 'And More...';

  return (
    <div className="group relative flex items-center justify-center">
      {/* Hover overlay (full cell) */}
      <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
        <div className="absolute inset-0 bg-slate-200/55" />
      </div>

      <div className="relative flex flex-col md:flex-row items-center gap-2 md:gap-4">
        {logoSrc && !logoFailed ? (
          <span className="inline-flex h-10 w-10 md:h-14 md:w-14 items-center justify-center text-slate-900">
            <img
              src={logoSrc}
              alt={name}
              className="h-8 w-8 md:h-11 md:w-11 object-contain"
              loading="lazy"
              onError={() => setLogoFailed(true)}
            />
          </span>
        ) : Mark ? (
          <span className="inline-flex h-10 w-10 md:h-14 md:w-14 items-center justify-center text-slate-900">
            <Mark className="h-8 w-8 md:h-11 md:w-11" />
          </span>
        ) : (
          <span className="inline-flex h-10 w-10 md:h-14 md:w-14" aria-hidden="true" />
        )}

        <span
          className={
            (isUpper
              ? 'text-[14px] md:text-[30px] font-extrabold tracking-[0.08em]'
              : isMore
                ? 'text-[12px] md:text-[22px] font-medium tracking-tight'
                  : 'text-[14px] md:text-[34px] font-medium tracking-tight') +
            ' text-slate-900 dark:text-white'
          }
          style={{ fontFamily: "'Martel Sans', sans-serif" }}
        >
          {name}
        </span>
      </div>
    </div>
  );
};

export const ProblemSection = () => {
  return (
    <section className="bg-[#f8f9fa] dark:bg-[#161616] py-12 md:py-24 lg:py-32" data-testid="problem-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-14 items-center">
          {/* Left Copy */}
          <div className="pr-0 lg:pr-10 text-center lg:text-left">
            <h2
              className="text-[24px] sm:text-3xl md:text-4xl lg:text-5xl xl:text-[56px] leading-[1.4] tracking-wide text-[#1a1a1a] dark:text-white"
              style={{ fontFamily: "'Martel Sans', sans-serif", fontWeight: 500 }}
            >
              <span className="md:hidden">
                Seamlessly integrates with your favorite{' '}
                <span className="text-[#7cb687]">AI-Powered builders.</span>
              </span>
              <span className="hidden md:inline">
                Seamlessly integrates
                <br />
                with your favorite <span className="text-[#9be38f]">AI-</span>
                <br />
                <span className="text-[#9be38f]">Powered builders.</span>
              </span>
            </h2>
          </div>

          {/* Right Grid */}
          <div className="relative">
            <div className="relative overflow-hidden rounded-[32px] bg-white dark:bg-[#282929]">
              {/* Base grid background */}
              <div
                className="pointer-events-none absolute inset-0 dark:hidden"
                style={{
                  backgroundImage:
                    [
                      // subtle repeating grid
                      'linear-gradient(to right, rgba(15,23,42,0.08) 1px, transparent 1px)',
                      'linear-gradient(to bottom, rgba(15,23,42,0.08) 1px, transparent 1px)',
                      // main 2-column divider
                      'linear-gradient(to right, transparent calc(50% - 0.75px), rgba(15,23,42,0.12) calc(50% - 0.75px), rgba(15,23,42,0.12) calc(50% + 0.75px), transparent calc(50% + 0.75px))',
                      // main 3-row dividers (1/3 and 2/3)
                      'linear-gradient(to bottom, transparent calc(33.333% - 0.75px), rgba(15,23,42,0.12) calc(33.333% - 0.75px), rgba(15,23,42,0.12) calc(33.333% + 0.75px), transparent calc(33.333% + 0.75px))',
                      'linear-gradient(to bottom, transparent calc(66.666% - 0.75px), rgba(15,23,42,0.12) calc(66.666% - 0.75px), rgba(15,23,42,0.12) calc(66.666% + 0.75px), transparent calc(66.666% + 0.75px))',
                    ].join(', '),
                  backgroundSize: '140px 120px, 140px 120px, 100% 100%, 100% 100%, 100% 100%',
                  backgroundPosition: '0 0, 0 0, 0 0, 0 0, 0 0',
                }}
              />
              <div
                className="pointer-events-none absolute inset-0 hidden dark:block"
                style={{
                  backgroundImage:
                    [
                      'linear-gradient(to right, rgba(255,255,255,0.08) 1px, transparent 1px)',
                      'linear-gradient(to bottom, rgba(255,255,255,0.08) 1px, transparent 1px)',
                      'linear-gradient(to right, transparent calc(50% - 0.75px), rgba(255,255,255,0.12) calc(50% - 0.75px), rgba(255,255,255,0.12) calc(50% + 0.75px), transparent calc(50% + 0.75px))',
                      'linear-gradient(to bottom, transparent calc(33.333% - 0.75px), rgba(255,255,255,0.12) calc(33.333% - 0.75px), rgba(255,255,255,0.12) calc(33.333% + 0.75px), transparent calc(33.333% + 0.75px))',
                      'linear-gradient(to bottom, transparent calc(66.666% - 0.75px), rgba(255,255,255,0.12) calc(66.666% - 0.75px), rgba(255,255,255,0.12) calc(66.666% + 0.75px), transparent calc(66.666% + 0.75px))',
                    ].join(', '),
                  backgroundSize: '140px 120px, 140px 120px, 100% 100%, 100% 100%, 100% 100%',
                  backgroundPosition: '0 0, 0 0, 0 0, 0 0, 0 0',
                }}
              />

              {/* Plus markers */}
              <div className="pointer-events-none absolute left-[44%] top-[28%] text-slate-300 dark:text-white/20">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                  <path d="M9 2v14M2 9h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </div>
              <div className="pointer-events-none absolute right-[6%] top-[8%] text-slate-300 dark:text-white/20">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                  <path d="M9 2v14M2 9h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </div>
              <div className="pointer-events-none absolute right-[10%] bottom-[10%] text-slate-300 dark:text-white/20">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                  <path d="M9 2v14M2 9h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </div>

              <div className="relative grid grid-cols-2 grid-rows-3 h-[320px] md:h-[520px]">
                {editorCards.map((card) => (
                  <EditorCell key={card.name} name={card.name} Mark={card.mark} logoSrc={card.logoSrc} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;
